/**
 * <MessagesBell /> — small header link to /messages with an unread badge.
 *
 * Signed-in only. Polls `GET /api/messages/unread-count` every 60s.
 * Kept intentionally minimal: clicking goes straight to the inbox — no
 * dropdown preview (that lives on /messages).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 60_000;

export default function MessagesBell() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user) { setCount(0); return undefined; }
    let mounted = true;
    const fetchCount = async () => {
      try {
        const { data } = await http.get("/messages/unread-count");
        if (mounted) setCount(data.unread || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => { mounted = false; clearInterval(id); };
  }, [user]);

  if (!user) return null;

  return (
    <Link
      to="/messages"
      data-testid="header-messages-link"
      aria-label={count > 0 ? `Messages, ${count} unread` : "Messages"}
      title="Messages"
      className="relative p-2 rounded-full hover:bg-black/[0.04] transition"
    >
      <MessageCircle className="w-5 h-5" />
      {count > 0 && (
        <span
          data-testid="header-messages-badge"
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-mono font-bold flex items-center justify-center px-1"
          style={{ background: "var(--wofy-primary)", color: "#fff" }}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}
